import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DefaultService, ReactionsResponse } from '../../generated-api';
import { EmojiCode } from './types';
import { EmojiPicker } from './EmojiPicker';
import { ReactionList } from './ReactionList';

interface ReactionModalProps {
  playId: string;
  isOpen: boolean;
  onClose: () => void;
  onReactionUpdate: () => void;
}

export const ReactionModal: React.FC<ReactionModalProps> = ({
  playId,
  isOpen,
  onClose,
  onReactionUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<ReactionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchReactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await DefaultService.getPlayReactionsPlaysPlayIdReactionsGet({ playId });
      setData(response);
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
      setError('Failed to load reactions');
    } finally {
      setLoading(false);
    }
  }, [playId]);

  useEffect(() => {
    if (isOpen) {
      fetchReactions();
    }
  }, [isOpen, fetchReactions]);

  const handleEmojiSelect = async (emoji: EmojiCode) => {
    try {
      setSubmitting(true);
      setError(null);

      // If user clicks the same emoji they already reacted with, remove the reaction
      if (data?.userReaction === emoji) {
        await DefaultService.removeReactionPlaysPlayIdReactionsDelete({ playId });
      } else {
        // Otherwise, add/change the reaction
        await DefaultService.addReactionPlaysPlayIdReactionsPost({ playId, requestBody: { emoji } });
      }

      onReactionUpdate();
      onClose();
    } catch (err: any) {
      console.error('Failed to update reaction:', err);
      if (err.status === 403) {
        setError('Please complete your player profile to react');
      } else {
        setError('Failed to update reaction');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-surface dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Error Message */}
          {error && (
            <div className="mx-4 mt-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Emoji Picker with Close Button */}
          <div className="border-b border-edge dark:border-gray-700 relative">
            <button
              onClick={onClose}
              className="absolute top-2 right-2 text-disabled hover:text-body dark:text-muted dark:hover:text-faint p-1 rounded-md hover:bg-surface-active dark:hover:bg-gray-700 z-10"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <EmojiPicker
              selectedEmoji={data?.userReaction || null}
              onEmojiSelect={handleEmojiSelect}
              disabled={submitting || loading}
            />
          </div>

          {/* Reaction List */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : (
              <ReactionList
                reactions={data?.reactions || []}
                currentUserId={data?.reactions.find(r => r.emoji === data?.userReaction)?.playerId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
